import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function ReviewsSection({ reviews, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", area: "", text: "", rating: 5 });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks! Your review will appear once approved.");
      setOpen(false);
      setForm({ name: "", area: "", text: "", rating: 5 });
      onSubmitted?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reviews" className="bg-[#0b1322] text-[#f0f4f8] py-24 md:py-32 border-b border-[#1e9bff]/15" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <span className="text-xs font-mono-lbl uppercase tracking-[0.2em] text-[#1e9bff] block mb-2">South Yorkshire Testimonials</span>
        <h2 className="font-display uppercase text-white text-[clamp(32px,4vw,52px)] leading-none mb-6">
          Customer Feedback
        </h2>

        {reviews.length === 0 ? (
          <div className="max-w-xl mx-auto p-10 rounded-2xl bg-[#070b12] border border-dashed border-[#1e9bff]/40 mb-8" data-testid="reviews-empty-state">
            <svg className="w-10 h-10 text-[#1e9bff] mx-auto mb-4 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-base text-white font-medium mb-2">Reviews from our South Yorkshire clients will appear here.</p>
            <p className="text-xs text-[#93b4d4]">Had your property cleaned by SY Exterior? Share your experience.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-left">
            {reviews.map((r) => (
              <article key={r.id} className="p-6 rounded-2xl bg-[#070b12] border border-[#1e9bff]/20" data-testid={`review-card-${r.id}`}>
                <div className="text-[#1e9bff] text-sm mb-3" aria-label={`${r.rating} out of 5 stars`}>
                  {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                </div>
                <p className="text-sm text-[#c8d9ea] mb-4">&ldquo;{r.text}&rdquo;</p>
                <p className="text-xs font-mono-lbl text-[#93b4d4] uppercase tracking-wider">
                  {r.name}{r.area ? ` · ${r.area}` : ""}
                </p>
              </article>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="leave-review-btn" className="inline-flex items-center gap-2 text-xs font-mono-lbl uppercase tracking-widest text-[#1e9bff] hover:underline">
              <span>Leave a Review</span>
              <span>→</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0b1322] border-[#1e9bff]/30 text-white" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase text-xl">Leave a Review</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input required minLength={2} placeholder="Your name" data-testid="review-name-input"
                  className="bg-[#070b12] border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:border-[#1e9bff] outline-none"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input placeholder="Area (e.g. S11)" data-testid="review-area-input"
                  className="bg-[#070b12] border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:border-[#1e9bff] outline-none"
                  value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
              </div>
              <div className="flex items-center gap-1" data-testid="review-rating-input">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}
                    data-testid={`review-star-${n}`}
                    className={`text-2xl transition-colors ${n <= form.rating ? "text-[#1e9bff]" : "text-white/20"}`}>
                    ★
                  </button>
                ))}
              </div>
              <textarea required minLength={10} rows={4} placeholder="How was your clean?" data-testid="review-text-input"
                className="w-full bg-[#070b12] border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:border-[#1e9bff] outline-none"
                value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
              <button type="submit" disabled={sending} data-testid="review-submit-btn"
                className="w-full py-3 rounded-xl bg-[#1e9bff] text-black font-display uppercase tracking-wider hover:bg-[#60baff] transition-colors disabled:opacity-50">
                {sending ? "Sending…" : "Submit Review"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
